import { useCallback, useEffect, useState } from 'react';

import { useAttachmentSignedUrl } from 'hooks/useAttachmentSignedUrl';
import { useToastActions } from 'stores/toastStore';

export function useClickAttachment(attachmentId: string, attachableModel?: string, page?: number) {
  const [attachmentToFetchId, setAttachmentToFetchId] = useState<string>('');

  const {
    data: attachmentData,
    isStale,
    isError,
    refetch,
  } = useAttachmentSignedUrl(attachmentToFetchId, attachableModel);
  const { addToast } = useToastActions();

  // Surface a failed signed-URL fetch instead of a silent dead click — e.g. the attachment's
  // attachable model isn't on the API allowlist yet, which 404s the request.
  useEffect(() => {
    if (isError) addToast({ text: 'Sorry, we couldn’t open that document. Please try again.' });
  }, [isError, addToast]);

  const openUrl = useCallback(
    (url: string) => {
      const target = typeof page === 'number' ? `${url}#page=${page}` : url;
      window.open(target, '_blank');
    },
    [page]
  );

  const openAttachment = useCallback(() => {
    if (isStale) {
      refetch();
    } else if (attachmentData?.url) {
      openUrl(attachmentData.url);
    } else {
      setAttachmentToFetchId(attachmentId);
    }
  }, [attachmentId, isStale, attachmentData?.url, refetch, openUrl]);

  useEffect(() => {
    if (attachmentData?.url) {
      openUrl(attachmentData.url);
    }
  }, [attachmentData?.url, openUrl]);

  return openAttachment;
}
